import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { ColorPicker, useBuiltInColorMap } from '@atlassian/color-picker';
import { CollapsibleItem, useProductDependencies } from '@atlassian/company-hub-common';

import type { LinksMenuExtensionActions } from '../../../../controllers/use-links-menu-parameters-and-actions';
import type { LinksMenuParameters } from '../../../../types';

import { messages } from './messages';

export type TextConfigProps = Pick<LinksMenuParameters['styles'], 'highlightColor' | 'textColor'> &
	Pick<LinksMenuExtensionActions, 'updateHighlightColor' | 'updateTextColor'> & {
		isReadableTextColor: (color: string) => boolean;
	};

export const TextConfig = ({
	highlightColor,
	updateHighlightColor,
	textColor,
	updateTextColor,
	isReadableTextColor,
}: TextConfigProps): React.JSX.Element => {
	const { isSSRProductInDarkMode } = useProductDependencies();
	const { branding } = useProductDependencies();
	const { hideDefaultColorPalette } = branding ?? {};
	const { highlightColorMap, textColorMap, emptyColorMap } =
		useBuiltInColorMap(isSSRProductInDarkMode);
	const { formatMessage } = useIntl();

	return (
		<CollapsibleItem title={formatMessage(messages.textTitle)}>
			<Stack space="space.200">
				<Inline spread="space-between" alignBlock="center">
					<Text color="color.text">
						<FormattedMessage {...messages.textColor} />
					</Text>
					<ColorPicker
						analyticsSource="linksMenuExtensionConfigTextColor"
						builtInColorMap={hideDefaultColorPalette ? emptyColorMap : textColorMap}
						isReadableColor={isReadableTextColor}
						onChange={updateTextColor}
						value={textColor}
						pickerTitle={messages.textColor}
						showCustomPalette
						showTextIcon
					/>
				</Inline>
				<Inline spread="space-between" alignBlock="center">
					<Text color="color.text">
						<FormattedMessage {...messages.selectedStateColor} />
					</Text>
					<ColorPicker
						analyticsSource="linksMenuExtensionConfigHighlightColor"
						builtInColorMap={hideDefaultColorPalette ? emptyColorMap : highlightColorMap}
						onChange={updateHighlightColor}
						value={highlightColor}
						pickerTitle={messages.selectedStateColor}
						showCustomPalette
					/>
				</Inline>
			</Stack>
		</CollapsibleItem>
	);
};
